import { useCallback, useEffect, useMemo, useState } from 'react';

/**
* A hook for safely fetching data using an arbitrary async fetch function. This helper encapsulates
* standard best practices that are easy to forget when using a `useEffect` to accomplish the same.
* @param fetcher The async fetch function. This function is provided an object with a boolean `cancelled`
*       property that can be interrogated at any point to see whether the request has been cancelled (due
*       to unmounting, a change of inputs, etc). This is useful for fetch functions which do multiple requests
*       in series and that could be cancelled before they finish.
* @param dependencies A react style array of dependencies. Since es-lint doesn't know about this hook,
*       it's important to make sure the correct dependencies are passed. Only when all dependencies have a
*       non-nullish value is the fetcher invoked. Until then, it is not loading and returns an `undefined` as its
*       result.
* @return {Object{ loading, refresh, result } }
*/
export function useAsyncFetch<T>(
  fetcher: ({ cancelledRef }: { cancelledRef: { cancelled: boolean } }) => Promise<T>,
  dependencies: unknown[],
): UseAsyncFetchResult<T> {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<T | undefined>(undefined);
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const memoizedFetcher = useMemo(() => fetcher, [...dependencies]);

  useEffect(() => {
    setResult(undefined);
    if (dependencies.some(dependency => dependency === undefined || dependency === null)) {
      setLoading(false);
      return; // not ready to load
    }
    setLoading(true);

    const cancelledRef = { cancelled: false };
    async function load() {
      let result;
      try {
        result = await memoizedFetcher({ cancelledRef });
      } finally {
        if (!cancelledRef.cancelled) {
          setResult(result);
          setLoading(false);
        }
      }
    }

    load().then(/* no-op */);
    return () => {
      cancelledRef.cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoizedFetcher, refreshTrigger, ...dependencies]);

  const refresh = useCallback(() => {
    setRefreshTrigger(val => val + 1);
  }, [setRefreshTrigger]);

  return {
    loading,
    refresh,
    result,
  };
}

interface UseAsyncFetchResult<T> {
  loading: boolean;
  refresh: () => void;
  result?: T;
}
